<template>
  <div class="app-login-confirm">
    <SimonModal
      :is-visible="showLoginConfirm"
      @hide="showLoginConfirm = false"
      id="login-confirm-modal"
      show-footer-buttons
      @btnLeft="showLoginConfirm = false"
      @btnRight="login"
      btn-right-title="Weiter"
      :disabled-btn-right="loading || loggingOut"
    >
      <template v-slot:modalTitle>
        <slot name="headline"><h4>Bitte gib dein Passwort ein</h4></slot>
      </template>
      <slot name="txtBefore"></slot>
      <div class="box">
        <b-form
          inline
          @submit.prevent="login()"
          :id="'form' + _uid"
          :novalidate="true"
          data-form-login
          class="login-form"
        >
          <b-form-group :id="'login-password_' + _uid" class="form-group-password mandatory mt-0" :class="vflowGetFeedbackClasses('password')">
            <label for="password"><slot name="lblPassword">Dein Passwort</slot></label>
            <b-form-input
              data-password
              ref="passwordInput"
              name="password"
              id="password"
              :type="passwordType"
              autocomplete="off"
              required
              :placeholder="passwordPlaceholder"
              v-model="password"
              @input="$v.password.$touch"
              @focus="vflowInputFocused('password'); blurred_ = true; displayErrorMessage = false; loginFailed = false;"
              @blur="vflowInputBlurred('password'); blurred_ = true"
              class="password-input"
              :disabled="loading || loggingOut"
            ></b-form-input>
            <img :src="passwordIcon" alt="show" height="24" width="24" class="icon" @click="togglePasswordVisibility()" />
            <slot name="txtAfterPassword"></slot>
          </b-form-group>
          <div class="error-box" v-if="displayErrorMessage" v-html="getBackendErrorMessage">
          </div>
        </b-form>
        <slot name="txtAfter"></slot>
      </div>
    </SimonModal>
  </div>
</template>

<script>
import { BForm, BFormGroup, BFormInput } from 'bootstrap-vue'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { onLogin } from '@/apollo'
import isValid from '@/mixins/validators/isValid'
import syncProperty from '@/mixins/syncProperty'
import validationFlow from '@/mixins/validators/validationFlow'
import gql from 'graphql-tag'
import { wrapQuery } from '@/helper/wrapGQLQuery'
import transaction from '@/mixins/transaction'
import logout from '@/mixins/logout'
import SimonModal from '@/components/modal/SimonModal.vue'
import showHidePassword from '@/mixins/showHidePassword'

export default {
  name: 'LoginConfirm',
  data () {
    return {
      callerPromise: null,
      resolver: null,
      rejecter: null,
      showLoginConfirm: false,
      currentCustomer: null,
      username: '',
      password: '',
      loading: false,
      displayErrorMessage: false,
      errorMsg: '',
      loginFailed: false,
      failCounter: 0,
      loggingOut: false
    }
  },
  props: {
    failedConfirmRedirectTo: {
      type: String,
      default: '/login?reason=locked'
    },
    passwordPlaceholder: {
      type: String,
      default: 'Passwort'
    }
  },
  mixins: [
    validationMixin,
    isValid,
    syncProperty('invalid', 'invalid_'),
    syncProperty('blurred', 'blurred_'),
    validationFlow,
    transaction('password-confirm'),
    showHidePassword(),
    logout
  ],
  watch: {
    '$v.$invalid': function (v) {
      this.invalid_ = v
      this.$emit('update:login-confirm-validation', v)
    }
  },
  mounted () {
    this.password = ''
    this.$v.$touch()
    this.invalid_ = this.$v.$invalid
  },
  components: {
    SimonModal,
    BFormGroup,
    BFormInput,
    BForm
  },
  computed: {
    getBackendErrorMessage: function () {
      return this.errorMsg
    }
  },
  methods: {
    letUserConfirmPassword () {
      this.password = ''
      this.vflowInputFocused('password')
      this.loginFailed = false
      this.displayErrorMessage = false
      this.showLoginConfirm = true
      const _this = this
      this.callerPromise = new Promise(function (resolve, reject) {
        _this.resolver = resolve
        _this.rejecter = reject
      })
      window.setTimeout(() => {
        this.$nextTick(() => {
          this.focusInput()
        })
      })
      return this.callerPromise
    },
    focusInput () {
      this.$refs.passwordInput.$el.focus()
    },
    passwordConfirmed () {
      this.resolver()
    },
    passwordConfirmFailed () {
      this.doLogout()
      this.rejecter(new Error('failed'))
      window.location.href = this.failedConfirmRedirectTo
    },
    login () {
      const _this = this
      this.vflowForceFeedback()
      let errorTrackingType = 'technical'
      if (typeof this.username !== 'undefined' && !this.$v.$error && !this.$v.$invalid) {
        const component = this
        this.loading = true
        this.$store.dispatch('login', { username: this.username.replace(/ /g, ''), password: this.password }).then(jwt => {
          // login for apollo client
          onLogin(component.$apolloProvider.defaultClient, jwt)
          this.displayErrorMessage = false
          this.errorMsg = ''
          this.showLoginConfirm = false
          this.loading = false
          _this.passwordConfirmed()
        }, error => {
          if (error.response && error.response?.status === 401) {
            if (error.response?.data && error.response?.data.code === 2000) {
              // account locked
              this.passwordConfirmFailed()
            } else {
              // unauthorized
              this.loginFailed = true
              let msg = 'Die Anmeldung schlug fehl!'
              if (error.response && error.response?.data) {
                msg = error.response.data?.message ? error.response.data?.message : ''
              }
              this.errorMsg = this.getSlotText('txtInvalidLoginConfirmPassword', msg)
              this.displayErrorMessage = true
              errorTrackingType = 'validation'
            }
          } else if (error.message || error.data?.message) {
            // any other error
            this.errorMsg = error.response.data?.message ? error.response.data?.message : error.message
            component.$store.commit('addError', { message: error.message })
            this.displayErrorMessage = true
            errorTrackingType = 'validation'
          } else {
            component.$store.commit('addError', 'technical error!')
            this.errorMsg = 'Uff! Ein technischer Fehler ist aufgetreten. Sorry dafür! Bitte versuche es später noch einmal.'
            this.displayErrorMessage = true
          }
          this.loading = false
          this.failCounter++
          if (this.failCounter === 3) {
            this.failCounter = 0
            _this.passwordConfirmFailed()
          }
          const data = {
            type: errorTrackingType,
            message: this.errorMsg
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        })
      } else {
        this.loading_ = false
        // ensure live feedback for validation errors
        this.vFlowGetFirstErrorElementName().then(function (result) {
          const data = {
            type: 'validation',
            message: 'required field missing',
            location: result + ' - unknown'
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        })
      }
    },
    doLogout () {
      this.loggingOut = true
      if (this.inWebView) {
        if (this.isIosDevice) {
          window.webkit.messageHandlers.reset.postMessage('logging out')
        } else {
          window.App.reset('logging out')
        }
      }
      this.logOut(this.failedConfirmRedirectTo)
    }
  },
  validations () {
    return {
      password: {
        required,
        notFailed: () => {
          return !this.loginFailed
        }
      }
    }
  },
  apollo: {
    currentCustomer: function () {
      return wrapQuery(
        gql`query currentCustomer {
           currentCustomer {
             contract {
               msisdn
             }
           }
        }`, this, 'currentCustomer', () => {
          this.username = this.currentCustomer?.contract?.msisdn
        }
      )
    }
  }
}
</script>
